import { loadDeferredMedia } from '@theme/looki-image-loading';

class LookiMediaTabs extends HTMLElement {
  connectedCallback() {
    this.setupFrame = requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    if (!this.isConnected || this.controller) return;
    this.tabs = Array.from(this.querySelectorAll('[data-mt-tab]'));
    if (!this.tabs.length) return;
    this.panels = Array.from(this.querySelectorAll('[data-mt-panel]'));
    this.scenes = Array.from(this.querySelectorAll('[data-mt-scene]'));
    // Reuse identical responsive backgrounds instead of hiding and revealing copies.
    const backgrounds = new Map();
    this.sceneTargets = this.scenes.map(scene => {
      if (scene.querySelector('video')) return scene;
      const picture = scene.querySelector('picture');
      const sources = picture ? Array.from(picture.querySelectorAll('source, img'), node => [
        node.getAttribute('media'), node.getAttribute('sizes'),
        node.getAttribute('data-looki-srcset') || node.getAttribute('srcset'),
        node.getAttribute('data-looki-src') || node.getAttribute('src'),
      ]) : scene.innerHTML;
      const key = JSON.stringify([scene.getAttribute('style') || '', sources]);
      if (!backgrounds.has(key)) backgrounds.set(key, scene);
      return backgrounds.get(key);
    });
    this.videos = Array.from(this.querySelectorAll('[data-mt-video]'));
    this.toggle = this.querySelector('[data-mt-toggle]');
    this.status = this.querySelector('[data-mt-status]');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.currentIndex = 0;
    this.selectionVersion = 0;
    this.userPaused = this.motion.matches;
    this.userStarted = false;
    this.hovered = false;
    this.focused = false;
    this.editorPaused = false;
    this.visible = !('IntersectionObserver' in window);

    this.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-mt-tab]');
      if (tab && this.tabs.includes(tab)) this.select(this.tabs.indexOf(tab), true);
      if (event.target.closest('[data-mt-toggle]') === this.toggle && this.toggle) {
        this.userPaused = !this.userPaused;
        this.userStarted = !this.userPaused;
        this.sync();
      }
    }, options);

    this.addEventListener('keydown', (event) => {
      const index = this.tabs.indexOf(event.target);
      if (index < 0) return;
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.tabs.length - 1;
      if (next === undefined) return;
      event.preventDefault();
      this.tabs[next].focus();
      this.select(next, true);
      this.tabs[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, options);

    this.addEventListener('mouseenter', () => { this.hovered = true; this.sync(); }, options);
    this.addEventListener('mouseleave', () => { this.hovered = false; this.sync(); }, options);
    this.addEventListener('focusin', () => { this.focused = true; this.sync(); }, options);
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.focused = false;
      this.sync();
    }, options);
    document.addEventListener('visibilitychange', () => this.sync(), options);
    this.motion.addEventListener('change', () => {
      this.userStarted = false;
      this.userPaused = this.motion.matches;
      this.sync();
    }, options);
    document.addEventListener('shopify:block:select', (event) => {
      const index = this.panels.findIndex(panel => panel.dataset.blockId === event.detail.blockId);
      if (index < 0) return;
      this.editorPaused = true;
      this.select(index, false, true);
    }, options);
    document.addEventListener('shopify:block:deselect', (event) => {
      if (!this.panels.some(panel => panel.dataset.blockId === event.detail.blockId)) return;
      this.editorPaused = false;
      this.sync();
    }, options);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.prepareMedia();
        this.sync();
      }, { threshold: 0.05 });
      this.observer.observe(this.querySelector('.mt-scenes') || this);
      this.preloadObserver = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        this.mediaNearby = true;
        this.prepareMedia();
      }, { rootMargin: '500px 0px' });
      this.preloadObserver.observe(this);
      const firstImage = document.querySelector('img[fetchpriority="high"]');
      firstImage?.addEventListener('load', () => this.prepareMedia(), options);
      firstImage?.addEventListener('error', () => this.prepareMedia(), options);
    }
    if (this.dataset.presentation === 'complete') {
      this.foregroundObserver = new ResizeObserver(() => this.syncForegroundSize());
      this.foregroundObserver.observe(this.querySelector('.mt-scenes'));
      this.foregroundObserver.observe(this.querySelector('.mt-tabs'));
    }
    const pendingIndex = this.tabs.findIndex(tab => tab.id === this.dataset.mtPendingTab);
    delete this.dataset.mtPendingTab;
    this.select(pendingIndex >= 0 ? pendingIndex : 0, pendingIndex >= 0);
  }

  syncForegroundSize() {
    if (this.dataset.presentation !== 'complete') return;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    if (mobile && this.dataset.mobileLayout === 'below') {
      this.style.removeProperty('--mt-foreground-max-height');
      return;
    }
    const styles = getComputedStyle(this);
    const inset = parseFloat(styles.getPropertyValue(mobile ? '--mt-mobile-inset' : '--mt-inset-y')) || 0;
    const gap = parseFloat(styles.getPropertyValue('--mt-content-gap')) || 0;
    const tabsHeight = mobile ? 0 : this.querySelector('.mt-tabs').getBoundingClientRect().height + gap;
    const available = this.querySelector('.mt-scenes').getBoundingClientRect().height - inset * 2 - tabsHeight;
    const height = `${Math.max(1, Math.floor(available))}px`;
    if (this.style.getPropertyValue('--mt-foreground-max-height') !== height) {
      this.style.setProperty('--mt-foreground-max-height', height);
    }
  }

  prepareMedia() {
    const firstImage = document.querySelector('img[fetchpriority="high"]');
    if (this.mediaReady || !this.mediaNearby || (!this.visible && firstImage && !firstImage.complete)) return;
    this.mediaReady = true;
    this.preloadObserver?.disconnect();
    this.select(this.currentIndex, false, true);
  }

  select(index, announce = false, load = announce || this.mediaReady || !('IntersectionObserver' in window)) {
    if (!this.tabs[index]) return;
    this.selectionController?.abort();
    this.selectionController = new AbortController();
    const signal = this.selectionController.signal;
    const version = ++this.selectionVersion;
    this.currentIndex = index;
    if (load) {
      loadDeferredMedia(this.panels[index]);
      loadDeferredMedia(this.sceneTargets[index]);
    }
    this.tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    });
    const activeScene = this.sceneTargets[index];
    if (announce && this.status) this.status.textContent = this.tabs[index].textContent.trim();
    const present = () => {
      if (signal.aborted || !this.isConnected || version !== this.selectionVersion) return;
      this.mediaPending = false;
      this.removeAttribute('aria-busy');
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        panel.inert = i !== index;
        panel.setAttribute('aria-hidden', String(i !== index));
      });
      this.scenes.forEach(scene => {
        const hidden = scene !== activeScene;
        if (scene.hidden !== hidden) scene.hidden = hidden;
      });
      this.syncForegroundSize();
      this.sync();
    };
    const pending = (load ? [...this.panels[index].querySelectorAll('img'), ...activeScene.querySelectorAll('img')] : [])
      .filter(image => !image.complete);
    // Retain the current preview during a weak-network download instead of showing an empty card.
    if (pending.length && this.panels.some((panel, i) => i !== index && !panel.hidden)) {
      this.mediaPending = true;
      this.setAttribute('aria-busy', 'true');
      this.sync();
      Promise.all(pending.map(image => new Promise(resolve => {
        const done = () => {
          image.removeEventListener('load', done);
          image.removeEventListener('error', done);
          signal.removeEventListener('abort', done);
          resolve();
        };
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
        signal.addEventListener('abort', done, { once: true });
        if (image.complete) done();
      }))).then(present);
    } else present();
  }

  sync() {
    window.clearTimeout(this.timer);
    const motionAllowed = !this.motion.matches || this.userStarted;
    const canRun = this.visible && !document.hidden && !this.userPaused && !this.mediaPending && motionAllowed;
    const rotate = canRun && this.dataset.autoplay === 'true' && this.tabs.length > 1 &&
      !this.focused && !this.editorPaused && !(this.hovered && this.dataset.pauseOnHover === 'true');
    this.dataset.running = String(rotate);
    if (this.toggle) {
      this.toggle.setAttribute('aria-pressed', String(this.userPaused));
      this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
    }
    this.videos.forEach(video => {
      const holder = video.closest('[data-mt-panel], [data-mt-scene]');
      const decorative = video.dataset.decorative === 'true';
      const play = !holder.hidden && canRun && this.dataset.videoAutoplay === 'true';
      video.hidden = decorative && !play;
      if (play) {
        const attempt = video.play();
        attempt?.catch(() => {
          if (decorative) video.hidden = true;
          else video.controls = true;
        });
      } else {
        video.pause();
      }
    });
    if (rotate) {
      this.timer = window.setTimeout(() => {
        this.select((this.currentIndex + 1) % this.tabs.length);
      }, Math.max(3000, Number(this.dataset.interval) || 5000));
    }
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.setupFrame);
    window.clearTimeout(this.timer);
    this.observer?.disconnect();
    this.preloadObserver?.disconnect();
    this.mediaNearby = false;
    this.mediaReady = false;
    this.selectionController?.abort();
    this.foregroundObserver?.disconnect();
    this.controller?.abort();
    this.controller = null;
    this.videos?.forEach(video => video.pause());
  }
}

if (!customElements.get('looki-media-tabs')) {
  customElements.define('looki-media-tabs', LookiMediaTabs);
}
